import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faInfoCircle,
  faComments, // Changed from faUserTie to match other components
} from "@fortawesome/free-solid-svg-icons";
import { getMotMinistre, deleteMotMinistre } from "../../services/api";
import { MotMinistre } from "../../types/types";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import AdminLayout from "./AdminLayout";

const MotMinistreList: React.FC = () => {
  const [motMinistres, setMotMinistres] = useState<MotMinistre[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { language } = useLanguage();

  useEffect(() => {
    fetchMotMinistres();
  }, []);

  const fetchMotMinistres = async () => {
    try {
      setLoading(true);
      const response = await getMotMinistre();
      setMotMinistres(response.data);
    } catch (error) {
      console.error("Error fetching mot du ministre:", error);
      toast.error("حدث خطأ أثناء جلب كلمة الوزير");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("هل أنت متأكد من حذف كلمة الوزير؟")) {
      try {
        await deleteMotMinistre(id);
        toast.success("تم حذف كلمة الوزير بنجاح");
        fetchMotMinistres();
      } catch (error) {
        console.error("Error deleting mot du ministre:", error);
        toast.error("حدث خطأ أثناء حذف كلمة الوزير");
      }
    }
  };

  const filteredMotMinistres = motMinistres.filter(
    (m) =>
      m.titrearabe.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.titrefrancais.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.textarabe.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.textfrancais.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const hasExistingRecord = motMinistres.length > 0;

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-mauritania-green-dark mb-2 flex items-center">
            <FontAwesomeIcon icon={faComments} className="ml-2" />
            {getTranslation("adminMotMinistreTitle", language)}
          </h1>
        </div>
        <Link
          to="/admin/mot-ministre/new"
          className={`py-2 px-4 rounded transition duration-300 ${
            hasExistingRecord
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-mauritania-green text-white hover:bg-mauritania-green-dark"
          }`}
          onClick={(e) => {
            if (hasExistingRecord) {
              e.preventDefault();
              toast.info("يوجد كلمة وزير بالفعل. يمكنك تعديلها فقط.");
            }
          }}
        >
          <FontAwesomeIcon icon={faPlus} className="ml-2" />
          {getTranslation("addMotMinistre", language)}
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="relative">
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:border-transparent"
            placeholder="ابحث في كلمة الوزير..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-mauritania-green"></div>
          <p className="mt-2 text-gray-600">
            {getTranslation("loadingData", language)}
          </p>
        </div>
      ) : filteredMotMinistres.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <p className="text-gray-600 mb-4">لا يوجد كلمة وزير مضاف حتى الآن</p>
          <Link
            to="/admin/mot-ministre/new"
            className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300"
          >
            <FontAwesomeIcon icon={faPlus} className="ml-2" />
            إضافة أول كلمة للوزير
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    العنوان (عربي)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    العنوان (فرنسي)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    النص العربي
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    النص الفرنسي
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMotMinistres.map((mot) => (
                  <tr key={mot.id}>
                    <td
                      className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate"
                      title={mot.titrearabe}
                    >
                      {mot.titrearabe}
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate"
                      title={mot.titrefrancais}
                    >
                      {mot.titrefrancais}
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate"
                      title={mot.textarabe}
                    >
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html:
                            mot.textarabe?.substring(0, 100) +
                              (mot.textarabe?.length > 100 ? "..." : "") || "",
                        }}
                      />
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate"
                      title={mot.textfrancais}
                    >
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html:
                            mot.textfrancais?.substring(0, 100) +
                              (mot.textfrancais?.length > 100 ? "..." : "") ||
                            "",
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link
                        to={`/admin/mot-ministre/edit/${mot.id}`}
                        className="text-mauritania-green hover:text-mauritania-green-dark mr-4"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                      <button
                        onClick={() => handleDelete(mot.id!)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default MotMinistreList;
